import { Home, Building2, CheckCircle2, AlertCircle, Flame, Zap, HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  dpeScale, categoryFields,
  constructionYears, timelineOptions, callbackTimes, revenueRanges,
} from '@/data/mock';

const ValidationMsg = ({ show, valid, message }) => {
  if (!show) return null;
  return (
    <p className={`validation-msg ${valid ? 'success' : 'error'}`} role="alert">
      {valid ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <AlertCircle className="h-3 w-3 flex-shrink-0" />}
      {message}
    </p>
  );
};

const CardSelector = ({ options, value, onChange, cols = 2, label }) => (
  <div className={`grid gap-2.5 ${cols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} role="radiogroup" aria-label={label}>
    {options.map((o) => (
      <button key={o.value} type="button" role="radio" aria-checked={value === o.value} data-testid={`option-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`relative min-h-[48px] p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${
          value === o.value ? 'border-[#0f766e] bg-teal-50 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}>
        {value === o.value && <CheckCircle2 className="absolute top-2.5 right-2.5 h-4 w-4 text-[#0f766e]" aria-hidden="true" />}
        <div className="font-semibold text-sm text-[#1f2937]">{o.label}</div>
        {o.desc && <div className="text-xs text-gray-400 mt-0.5">{o.desc}</div>}
      </button>
    ))}
  </div>
);

const SectionLabel = ({ children, htmlFor }) => (
  <Label htmlFor={htmlFor} className="text-sm font-semibold text-[#1f2937] mb-3 block">{children}</Label>
);

/* ── Step 0 : Contact essentiel ─────────────────────────── */
const ContactStep = ({ formData, onChange }) => {
  const phoneTouched = formData.phone !== undefined && formData.phone !== '';
  const phoneClean = formData.phone?.replace(/\s/g, '') || '';
  const phoneValid = phoneClean.length >= 10;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <SectionLabel htmlFor="lastName">Nom</SectionLabel>
          <Input id="lastName" data-testid="input-lastName" autoComplete="family-name"
            value={formData.lastName || ''} onChange={(e) => onChange('lastName', e.target.value)}
            placeholder="Dupont" aria-required="true"
            className={`h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] ${formData.lastName ? 'input-success' : ''}`} />
        </div>
        <div>
          <SectionLabel htmlFor="firstName">Prénom</SectionLabel>
          <Input id="firstName" data-testid="input-firstName" autoComplete="given-name"
            value={formData.firstName || ''} onChange={(e) => onChange('firstName', e.target.value)}
            placeholder="Jean" aria-required="true"
            className={`h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] ${formData.firstName ? 'input-success' : ''}`} />
        </div>
      </div>

      <div>
        <SectionLabel htmlFor="phone">Téléphone</SectionLabel>
        <Input id="phone" data-testid="input-phone" type="tel" autoComplete="tel" inputMode="tel"
          value={formData.phone || ''} onChange={(e) => onChange('phone', e.target.value)}
          placeholder="06 12 34 56 78" aria-required="true" aria-invalid={phoneTouched && !phoneValid}
          className={`h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] ${phoneTouched ? (phoneValid ? 'input-success' : 'input-error') : ''}`} />
        <ValidationMsg show={phoneTouched && !phoneValid} valid={false} message="10 chiffres minimum" />
      </div>

      <div>
        <SectionLabel>Vous êtes</SectionLabel>
        <CardSelector
          options={[
            { value: 'proprietaire', label: 'Propriétaire', desc: 'Vous êtes propriétaire du logement' },
            { value: 'locataire', label: 'Locataire', desc: 'Vous louez le logement' },
            { value: 'bailleur', label: 'Bailleur', desc: 'Vous louez à un tiers' },
          ]}
          value={formData.status}
          onChange={(v) => onChange('status', v)}
          cols={3}
          label="Votre statut"
        />
      </div>

      <div>
        <SectionLabel htmlFor="department">Département</SectionLabel>
        <Input id="department" data-testid="input-department" type="text" inputMode="numeric" maxLength={3}
          value={formData.department || ''} onChange={(e) => onChange('department', e.target.value.replace(/\D/g, ''))}
          placeholder="Ex : 75" aria-required="true"
          className={`h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] ${formData.department?.length >= 2 ? 'input-success' : ''}`} />
      </div>
    </div>
  );
};

/* ── Step 1 : Logement ──────────────────────────────────── */
const LogementStep = ({ formData, onChange }) => (
  <div className="space-y-5">
    <div>
      <SectionLabel htmlFor="address">Adresse</SectionLabel>
      <Input id="address" data-testid="input-address" autoComplete="street-address"
        value={formData.address || ''} onChange={(e) => onChange('address', e.target.value)}
        placeholder="12 rue de la Paix"
        className="h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e]" />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <SectionLabel htmlFor="postal">Code postal</SectionLabel>
        <Input id="postal" data-testid="input-postal" inputMode="numeric" maxLength={5}
          value={formData.postal || ''} onChange={(e) => onChange('postal', e.target.value.replace(/\D/g, ''))}
          placeholder="75001" className="h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e]" />
      </div>
      <div>
        <SectionLabel htmlFor="city">Ville</SectionLabel>
        <Input id="city" data-testid="input-city" autoComplete="address-level2"
          value={formData.city || ''} onChange={(e) => onChange('city', e.target.value)}
          placeholder="Paris" className="h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e]" />
      </div>
    </div>

    <div>
      <SectionLabel>Type de logement</SectionLabel>
      <CardSelector
        options={[
          { value: 'maison', label: 'Maison', desc: 'Individuelle' },
          { value: 'appartement', label: 'Appartement', desc: 'Copropriété' },
        ]}
        value={formData.housingType} onChange={(v) => onChange('housingType', v)}
        cols={2} label="Type de logement"
      />
      {formData.housingType === 'appartement' && (
        <div data-testid="disqualification-msg" className="mt-3 bg-red-50 border border-red-200 rounded-xl p-4 text-center" role="alert">
          <AlertCircle className="h-5 w-5 text-red-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-red-700 mb-1">
            Désolé, vous n'êtes pas éligible
          </p>
          <p className="text-xs text-red-500">
            Le dispositif MaPrimeRénov' pour ce type de travaux ne concerne que les maisons individuelles. Les appartements en copropriété ne sont pas éligibles à cette aide.
          </p>
        </div>
      )}
    </div>

    {formData.housingType !== 'appartement' && (
      <>
        <div>
      <SectionLabel>Chauffage existant</SectionLabel>
      <CardSelector
        options={[
          { value: 'fioul', label: 'Fioul', desc: 'Chaudière fioul' },
          { value: 'gaz', label: 'Gaz', desc: 'Chaudière gaz' },
          { value: 'electrique', label: 'Électrique', desc: 'Convecteurs' },
          { value: 'autre', label: 'Autre', desc: 'Bois, PAC...' },
        ]}
        value={formData.heatingType} onChange={(v) => onChange('heatingType', v)}
        cols={2} label="Chauffage existant"
      />
    </div>

    <div>
      <SectionLabel htmlFor="livingSurface">Superficie (m²)</SectionLabel>
      <div className="relative">
        <Input id="livingSurface" data-testid="input-livingSurface" type="number" inputMode="numeric" min="1"
          value={formData.livingSurface || ''} onChange={(e) => onChange('livingSurface', e.target.value)}
          placeholder="Ex : 90"
          className="h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] pr-10" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" aria-hidden="true">m²</span>
      </div>
    </div>
      </>
    )}
  </div>
);

/* ── Step 2 : Projet ────────────────────────────────────── */
const ProjetStep = ({ formData, onChange, category }) => {
  const fields = categoryFields[category?.id] || [];
  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>Année de construction</SectionLabel>
        <CardSelector options={constructionYears} value={formData.constructionYear}
          onChange={(v) => onChange('constructionYear', v)} cols={2} label="Construction" />
      </div>

      <div>
        <SectionLabel>DPE actuel</SectionLabel>
        <div className="space-y-1" role="radiogroup" aria-label="DPE">
          {dpeScale.map((d) => (
            <button key={d.letter} type="button" role="radio" aria-checked={formData.dpe === d.letter}
              disabled={!d.selectable} data-testid={`dpe-${d.letter}`}
              onClick={() => d.selectable && onChange('dpe', d.letter)}
              className={`flex items-center gap-3 w-full min-h-[36px] rounded-lg py-1 transition-all duration-200 ${
                d.selectable ? 'cursor-pointer hover:pl-1' : 'opacity-35 cursor-not-allowed'
              } ${formData.dpe === d.letter ? 'pl-1' : ''}`}>
              <div className={`h-7 rounded flex items-center px-2.5 text-white font-bold text-xs transition-all duration-200 ${
                formData.dpe === d.letter ? 'ring-2 ring-[#0f766e] ring-offset-2 scale-105' : ''
              }`} style={{ backgroundColor: d.color, width: d.w }} aria-hidden="true">{d.letter}</div>
              {d.selectable && <span className="text-xs text-gray-600">{d.label}</span>}
            </button>
          ))}
        </div>
        <button type="button" role="radio" aria-checked={formData.dpe === 'unknown'} data-testid="dpe-unknown"
          onClick={() => onChange('dpe', 'unknown')}
          className={`w-full mt-2 min-h-[44px] p-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
            formData.dpe === 'unknown' ? 'border-[#0f766e] bg-teal-50 text-[#0f766e]' : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}>Je ne connais pas mon DPE</button>
      </div>

      {fields.map((field) => (
        <div key={field.id}>
          <SectionLabel htmlFor={field.type === 'number' ? field.id : undefined}>{field.label}</SectionLabel>
          {field.type === 'cards' && <CardSelector options={field.options} value={formData[field.id]}
            onChange={(v) => onChange(field.id, v)} cols={2} label={field.label} />}
          {field.type === 'number' && (
            <div className="relative">
              <Input id={field.id} data-testid={`input-${field.id}`} type="number" inputMode="numeric" min="1"
                value={formData[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                className="h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] pr-12" />
              {field.unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{field.unit}</span>}
            </div>
          )}
        </div>
      ))}

      <div>
        <SectionLabel>Délai souhaitée</SectionLabel>
        <CardSelector options={timelineOptions} value={formData.timeline}
          onChange={(v) => onChange('timeline', v)} cols={2} label="Délai" />
      </div>

      <div>
        <SectionLabel>Avez-vous un devis ?</SectionLabel>
        <CardSelector
          options={[{ value: 'oui', label: 'Oui', desc: "J'ai un devis" }, { value: 'non', label: 'Non', desc: 'Pas encore' }]}
          value={formData.hasQuote} onChange={(v) => onChange('hasQuote', v)} cols={2} label="Devis" />
      </div>
    </div>
  );
};

/* ── Step 3 : Qualification finale ──────────────────────── */
const QualifFinalStep = ({ formData, onChange }) => {
  const emailTouched = formData.email !== undefined && formData.email !== '';
  const emailValid = formData.email?.includes('@') && formData.email?.includes('.');

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>Revenu fiscal de référence</SectionLabel>
        <div className="space-y-2" role="radiogroup" aria-label="Revenu fiscal">
          {revenueRanges.map((r) => (
            <button key={r.id} type="button" role="radio" aria-checked={formData.revenueRange === r.id}
              data-testid={`revenue-${r.id}`} onClick={() => onChange('revenueRange', r.id)}
              className={`w-full min-h-[48px] p-3 rounded-xl border-2 text-left transition-all duration-200 flex items-center gap-3 ${
                formData.revenueRange === r.id ? `${r.borderSel} ${r.bgSel} shadow-sm` : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}>
              <div className={`w-3 h-3 rounded-full ${r.colorDot} flex-shrink-0`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-[#1f2937]">{r.label}</span>
                <span className="text-xs text-gray-400 ml-1.5 hidden sm:inline">{r.range}</span>
              </div>
              <span className={`text-[0.6rem] sm:text-[0.65rem] font-semibold ${r.textColor} whitespace-nowrap`}>{r.tag}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <SectionLabel htmlFor="email">Adresse email</SectionLabel>
        <Input id="email" data-testid="input-email" type="email" autoComplete="email" inputMode="email"
          value={formData.email || ''} onChange={(e) => onChange('email', e.target.value)}
          placeholder="jean.dupont@email.fr" aria-required="true"
          className={`h-12 rounded-xl border-gray-200 focus:border-[#0f766e] focus:ring-[#0f766e] ${
            emailTouched ? (emailValid ? 'input-success' : 'input-error') : ''
          }`} />
        <ValidationMsg show={emailTouched && !emailValid} valid={false} message="Email invalide" />
      </div>

      <div>
        <SectionLabel>Meilleur créneau de rappel</SectionLabel>
        <CardSelector options={callbackTimes} value={formData.callbackTime}
          onChange={(v) => onChange('callbackTime', v)} cols={3} label="Créneau" />
      </div>

      <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100" role="note">
        <p className="text-xs text-gray-500 leading-relaxed">
          Vos données sont utilisées uniquement pour vous recontacter. Conformément au RGPD,
          vous pouvez demander leur suppression à tout moment.
        </p>
      </div>
    </div>
  );
};

/* ── Main dispatcher ────────────────────────────────────── */
export const StepContent = ({ step, formData, onChange, category }) => {
  switch (step) {
    case 0: return <ContactStep formData={formData} onChange={onChange} />;
    case 1: return <LogementStep formData={formData} onChange={onChange} />;
    case 2: return <ProjetStep formData={formData} onChange={onChange} category={category} />;
    case 3: return <QualifFinalStep formData={formData} onChange={onChange} />;
    default: return null;
  }
};
